import React, { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { usePolling } from "@/hooks/usePolling";
import { useAuth } from "@/context/AuthContext";
import { TIPO } from "@/lib/constants";
import { TIPO_EN, MOTIVO_PENDIENTE_EN, horaEn, fechaHoraEn, estadoChofer, proximoPasoChofer, misEtapas, numeroEtapa, docsPendientes } from "@/lib/chofer";
import { ContainerId } from "@/components/marca/ContainerId";
import { EmptyState } from "@/components/marca/Layout";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Truck, CircleCheckBig, ChevronRight, Loader2, AlertTriangle, ArrowRight, ArrowDownToLine, ArrowUpFromLine } from "lucide-react";
import { toast } from "sonner";
import { t, getIdioma } from "@/lib/i18n";

// Fix Q (2026-10-13): lista de cargas del chofer en inglés (Today / Upcoming). Cada carga muestra en qué paso va ESTE chofer
// («Step 2 of 5», «Waiting for previous steps», «Your steps are done», «Completed») y si le faltan documentos.
const NAVY = "#223B59";
const COLOR_ESTADO = { in_progress: "#244C79", ready: "#244C79", waiting: "#D4A017", my_part_done: "#277A3F", completed: "#277A3F", cancelled: "#7E838C" };
const diaCortoEn = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Intl.DateTimeFormat(getIdioma() === "es" ? "es" : "en-US", { weekday: "long", month: "short", day: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(y, m - 1, d, 12)));
};
const terminada = (est) => ["completed", "my_part_done", "cancelled"].includes(est.k);
// Hora de la próxima etapa de este chofer (o la de la carga) y fecha en la que le toca.
const proximaEtapaMia = (c, uid) => misEtapas(c, uid).find((e) => !e.salida_at) || null;
const horaCarga = (c, uid) => horaEn(proximaEtapaMia(c, uid)?.hora_desde || c.hora_inicio || "");
const fechaCarga = (c, uid) => proximaEtapaMia(c, uid)?.fecha || c.fecha || "";

function TipoChip({ c }) {
  const tt = TIPO[c.tipo_operacion];
  const Icon = c.tipo_operacion === "importacion" ? ArrowDownToLine : ArrowUpFromLine;
  if (!tt) return null;
  return (
    <span data-testid={`chofer-tipo-${c.id}`} className={cn("inline-flex items-center gap-1 rounded-[6px] font-display font-bold tracking-[.08em] whitespace-nowrap text-[11.5px] px-2 py-[3px]", tt.badge)}>
      <Icon className="h-3.5 w-3.5" strokeWidth={2.4} />{TIPO_EN[c.tipo_operacion]}{c.tamano ? ` · ${c.tamano}` : ""}
    </span>
  );
}

function EstadoChip({ c, est }) {
  return (
    <span data-testid={`chofer-estado-${c.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-surface-2 border border-line px-2.5 py-[3px] text-[12.5px] font-semibold text-ink-2 whitespace-nowrap">
      <span className="h-[7px] w-[7px] rounded-full shrink-0" style={{ background: COLOR_ESTADO[est.k] }} />
      <span className={est.k === "cancelled" ? "line-through" : ""}>{est.label}</span>
    </span>
  );
}

export default function ChoferHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.id;
  const { setResumen } = useOutletContext();
  const [tab, setTab] = useState("hoy");
  const [cargas, setCargas] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [cerrarOpen, setCerrarOpen] = useState(false);

  const pedido = useRef(0);
  const cargar = useCallback(async () => {
    const mia = ++pedido.current;
    try {
      const { data } = await api.get(`/chofer/cargas?tab=${tab}`);
      if (mia !== pedido.current) return; // Fix Q (2026-10-13): respuesta de la pestaña anterior; no se muestra
      setCargas(data);
      setLoaded(true);
    } catch { /* ignore transient */ }
  }, [tab]);

  usePolling(cargar, 30000);

  // Fix Q (2026-10-13): al cambiar de pestaña la lista se pide en ese momento. Antes seguía mostrando la de «Today» hasta el
  // siguiente refresco (hasta 30 segundos), porque usePolling solo pide al montar y cada 30 s.
  const primera = useRef(true);
  useEffect(() => {
    if (primera.current) { primera.current = false; return; }
    cargar();
  }, [cargar]);

  useEffect(() => {
    if (tab === "hoy" && loaded) setResumen(cargas.length);
    else setResumen("");
  }, [tab, loaded, cargas.length, setResumen]);

  const conEstado = cargas.map((c) => ({ c, est: estadoChofer(c, uid) }));
  const activasHoy = tab === "hoy" ? conEstado.filter((x) => !terminada(x.est)) : [];
  const ahora = activasHoy.find((x) => x.est.k === "in_progress") || activasHoy.find((x) => x.est.k === "ready") || activasHoy[0] || null;
  const despues = activasHoy.filter((x) => x !== ahora);
  const hechas = tab === "hoy" ? conEstado.filter((x) => terminada(x.est)) : [];
  const porFecha = tab === "proximas"
    ? conEstado.reduce((acc, x) => { const f = fechaCarga(x.c, uid); (acc[f] = acc[f] || []).push(x); return acc; }, {})
    : {};

  return (
    <div className="max-w-md mx-auto px-4">
      <div className="grid grid-cols-2 gap-1 p-1 rounded-lg bg-[#E9E5DD] mb-5">
        {[{ k: "hoy", l: t("Today") }, { k: "proximas", l: t("Upcoming") }].map((tt) => (
          <button
            key={tt.k}
            data-testid={`chofer-tab-${tt.k}`}
            onClick={() => { setTab(tt.k); setLoaded(false); setCargas([]); }}
            className={cn("h-12 rounded-md text-[15px] font-semibold transition-colors duration-150", tab === tt.k ? "bg-white text-ink shadow-[0_1px_2px_rgba(22,24,29,.08)]" : "text-ink-2")}
          >{tt.l}</button>
        ))}
      </div>

      {!loaded && (
        <div className="space-y-3"><Skeleton className="h-64 rounded-[14px]" /><Skeleton className="h-20 rounded-xl" /></div>
      )}

      {loaded && cargas.length === 0 && (
        <div data-testid="chofer-vacio">
          <EmptyState icon={Truck} texto={tab === "hoy" ? t("You have no loads assigned for today") : t("You have no upcoming loads")} />
        </div>
      )}

      {ahora && <CargaAhora c={ahora.c} est={ahora.est} uid={uid} onOpen={() => navigate(`/chofer/cargas/${ahora.c.id}`)} />}

      {despues.length > 0 && (
        <>
          <p className="eyebrow mt-6 mb-2">{t("Later")}</p>
          <div className="space-y-2">{despues.map((x) => <CargaCompacta key={x.c.id} c={x.c} est={x.est} uid={uid} navigate={navigate} />)}</div>
        </>
      )}

      {hechas.length > 0 && (
        <>
          <p className="eyebrow mt-6 mb-2">{t("Done today")}</p>
          <div className="space-y-2">{hechas.map((x) => <CargaCompacta key={x.c.id} c={x.c} est={x.est} uid={uid} navigate={navigate} />)}</div>
        </>
      )}

      {Object.keys(porFecha).sort().map((f) => (
        <div key={f} className="mb-4">
          <p className="eyebrow mt-4 mb-2">{f ? diaCortoEn(f) : t("No date")}</p>
          <div className="space-y-2">{porFecha[f].map((x) => <CargaCompacta key={x.c.id} c={x.c} est={x.est} uid={uid} navigate={navigate} />)}</div>
        </div>
      ))}

      {tab === "hoy" && (
        <Button data-testid="cerrar-dia-btn" onClick={() => setCerrarOpen(true)} variant="outline" className="w-full h-14 mt-6 border-2 border-dashed border-line-strong text-base">
          <CircleCheckBig className="h-5 w-5" />{t(" Close my day")}</Button>
      )}

      {cerrarOpen && <CerrarDiaDialog onClose={() => setCerrarOpen(false)} onGoCarga={(id) => { setCerrarOpen(false); navigate(`/chofer/cargas/${id}`); }} />}
    </div>
  );
}

// Recorrido del chofer: un punto por etapa propia, con su nombre debajo (el color nunca va solo).
function Progreso({ carga, uid }) {
  const ets = misEtapas(carga, uid);
  const idx = ets.findIndex((e) => !e.salida_at);
  return (
    <ol className="flex items-start w-full overflow-x-auto" data-testid="chofer-progreso">
      {ets.map((e, i) => {
        const hecha = !!e.salida_at;
        const cur = i === idx;
        return (
          <li key={e.clave} className="flex-1 min-w-[72px] flex flex-col items-center relative">
            {i > 0 && <span className="absolute top-[6px] right-1/2 w-full h-[2px]" style={{ background: i <= idx || idx === -1 ? NAVY : "#E5E1D9" }} />}
            <span className="relative z-10 h-[14px] w-[14px] rounded-full border-2"
              style={{ borderColor: hecha ? NAVY : cur ? "#244C79" : "#D3CDC2", background: hecha ? NAVY : cur ? "#244C79" : "#fff", boxShadow: cur ? "0 0 0 4px #244C7933" : "none" }} />
            <span className={cn("mt-2 text-[11.5px] leading-tight text-center px-1", cur ? "font-semibold text-ink" : "text-ink-2")}>{t(e.nombre)}</span>
          </li>
        );
      })}
    </ol>
  );
}

function CargaAhora({ c, est, uid, onOpen }) {
  const sig = proximaEtapaMia(c, uid);
  const total = misEtapas(c, uid).length;
  const eta = c.eta_vigente;
  const faltan = docsPendientes(c, uid);
  return (
    <section data-testid={`chofer-carga-${c.id}`} onClick={onOpen} className="bg-white border border-line rounded-[14px] overflow-hidden cursor-pointer">
      <div className="h-[6px]" style={{ background: TIPO[c.tipo_operacion]?.color }} />
      <div className="p-4">
        <p className="eyebrow mb-3">{t("Now")}</p>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <TipoChip c={c} />
          <EstadoChip c={c} est={est} />
        </div>
        <div className="mt-3">{c.numero_contenedor ? <ContainerId numero={c.numero_contenedor} size="xl" digitoLabel={t("check digit")} /> : <span className="font-display font-bold text-[26px]">{c.load_id || t("Container pending pickup")}</span>}</div>
        {sig && (
          <p className="text-[14px] text-ink-3" data-testid={`chofer-paso-${c.id}`}>{t("Step {v0} of {total} · {nombre}{v3}", { v0: numeroEtapa(c, sig.clave, uid), total, nombre: t(sig.nombre), v3: sig.lugar ? ` · ${sig.lugar}` : "" })}
          </p>
        )}
        {eta && <p className="text-[14px] text-ink-2 mt-0.5" data-testid={`chofer-eta-${c.id}`}>{t("Delivery ETA: {v0}", { v0: fechaHoraEn(eta.eta, eta.zona_horaria) })}</p>}
        {faltan.length > 0 && (
          <p className="mt-2 inline-flex items-center gap-1 text-[13px] font-semibold text-danger" data-testid={`chofer-docs-pendientes-${c.id}`}>
            <AlertTriangle className="h-4 w-4" />{t(" Documents pending")}</p>
        )}
        <div className="mt-5"><Progreso carga={c} uid={uid} /></div>
        <button
          data-testid={`chofer-abrir-${c.id}`}
          onClick={onOpen}
          className="mt-5 w-full min-h-[54px] rounded-lg bg-brand text-white px-4 py-2 text-left flex items-center justify-between"
        >
          <span>
            <span className="block eyebrow !text-white/80 !text-[10.5px]">{t("Next step")}</span>
            <span className="block font-semibold text-[16px]">{proximoPasoChofer(c, uid)}</span>
          </span>
          <ArrowRight className="h-5 w-5" />
        </button>
      </div>
    </section>
  );
}

function CargaCompacta({ c, est, uid, navigate }) {
  const faltan = docsPendientes(c, uid);
  const hora = horaCarga(c, uid);
  return (
    <button
      data-testid={`chofer-carga-${c.id}`}
      onClick={() => navigate(`/chofer/cargas/${c.id}`)}
      className="w-full text-left bg-white border border-line rounded-xl overflow-hidden flex items-stretch min-h-[64px]"
    >
      <span className="w-1 shrink-0" style={{ background: TIPO[c.tipo_operacion]?.color }} />
      <span className="flex-1 min-w-0 px-3 py-3">
        <span className="flex items-center justify-between gap-2">
          {c.numero_contenedor ? <ContainerId numero={c.numero_contenedor} size="md" digitoLabel={t("check digit")} /> : <span className="font-display font-bold text-[18px]">{c.load_id || t("Container pending pickup")}</span>}
          {hora !== "—" && <span className="font-mono text-[14px] text-ink-2">{hora}</span>}
        </span>
        <span className="flex items-center gap-2 mt-1.5 flex-wrap">
          <EstadoChip c={c} est={est} />
          {faltan.length > 0 && (
            <span data-testid={`chofer-docs-pendientes-${c.id}`} className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-danger"><AlertTriangle className="h-3.5 w-3.5" />{t(" Documents pending")}</span>
          )}
        </span>
      </span>
      <ChevronRight className="h-5 w-5 text-ink-3 self-center mr-2 shrink-0" />
    </button>
  );
}

// «Close my day». Si falta algo que SÍ bloquea (faltantes) se muestra la lista; si el día se cierra con documentos por subir
// (documentos_pendientes) se avisa, pero el día queda cerrado y los documentos siguen pendientes para la oficina.
function CerrarDiaDialog({ onClose, onGoCarga }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendiente, setPendiente] = useState(null); // {carga_id, tipo, contenedor}

  const intentar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/chofer/jornada/cerrar");
      const docs = data.documentos_pendientes || [];
      if (data.cerrada && docs.length === 0) { toast.success(t("Your day is closed")); onClose(); return; }
      setResult({ ...data, faltantes: data.faltantes || [], documentos_pendientes: docs });
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [onClose]);

  useEffect(() => { intentar(); }, [intentar]);

  const docs = result?.documentos_pendientes || [];
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{result?.cerrada ? t("Your day is closed") : t("Close my day")}</DialogTitle></DialogHeader>
        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-ink-3" /></div>
        ) : result ? (
          <div className="space-y-4">
            {result.sin_empezar > 0 && (
              <p data-testid="cerrar-sin-empezar" className="text-sm text-ink-2 bg-surface-2 rounded-lg p-3">{(result.sin_empezar === 1 ? t("You have 1 load you have not started.") : t("You have {n} loads you have not started.", { n: result.sin_empezar }))}</p>
            )}
            {!result.cerrada && result.faltantes.length > 0 && (
              <>
                <p className="text-sm font-bold text-danger flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{t(" You are missing:")}</p>
                <div className="space-y-2">
                  {result.faltantes.map((f, i) => (
                    <div key={i} data-testid="faltante-item" className="rounded-lg border-2 border-[#E8B9B8] bg-[#FBEDEC] p-3">
                      <p className="text-sm font-semibold text-ink"><ContainerId numero={f.contenedor} size="sm" digitoLabel={t("check digit")} /> — {f.mensaje}</p>
                      <div className="flex gap-2 mt-2">
                        <Button size="sm" variant="outline" className="h-10 border-[#E8B9B8]" onClick={() => onGoCarga(f.carga_id)}>{t("Go to load ")}<ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                        <Button size="sm" data-testid="no-puedo-subirlo" className="h-10 bg-brand hover:bg-brand-dark text-white" onClick={() => setPendiente({ carga_id: f.carga_id, tipo: f.tipo, contenedor: f.contenedor })}>{t("I can’t upload it")}</Button>
                      </div>
                    </div>
                  ))}
                </div>
                <Button data-testid="cerrar-reintentar" onClick={intentar} className="w-full h-12 bg-brand hover:bg-brand-dark text-white">{t("Try closing again")}</Button>
              </>
            )}
            {docs.length > 0 && (
              <div data-testid="cerrar-docs-pendientes" className="rounded-lg border-2 border-[#E8B9B8] bg-[#FBEDEC] p-3">
                <p className="text-sm font-bold text-danger flex items-center gap-2"><AlertTriangle className="h-4 w-4" />{t(" Documents pending")}</p>
                <p className="text-[13px] text-ink-2 mt-1">{result.cerrada ? t("Your day is closed. Please upload these documents as soon as you can:") : t("You can still upload these documents:")}</p>
                <ul className="mt-2 space-y-2">
                  {docs.map((d, i) => (
                    <li key={i} data-testid="cerrar-doc-item" className="flex items-center justify-between gap-2">
                      <span className="text-sm text-ink min-w-0"><ContainerId numero={d.contenedor} size="sm" digitoLabel={t("check digit")} /> — {d.nombre}</span>
                      <Button size="sm" variant="outline" className="h-9 shrink-0 border-[#E8B9B8]" onClick={() => onGoCarga(d.carga_id)}>{t("Add")}</Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.cerrada && (
              <Button data-testid="cerrar-listo" onClick={onClose} className="w-full h-12 bg-brand hover:bg-brand-dark text-white">{t("OK")}</Button>
            )}
            {!result.cerrada && result.faltantes.length === 0 && docs.length === 0 && (
              <p className="text-sm text-ok">{t("Everything is in order.")}</p>
            )}
          </div>
        ) : null}

        {pendiente && (
          <PendienteForm
            pendiente={pendiente}
            onClose={() => setPendiente(null)}
            onDone={() => { setPendiente(null); intentar(); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function PendienteForm({ pendiente, onClose, onDone }) {
  const [motivo, setMotivo] = useState("");
  const [texto, setTexto] = useState("");
  const [saving, setSaving] = useState(false);
  const guardar = async () => {
    if (!motivo) return toast.error(t("Choose a reason"));
    setSaving(true);
    try {
      await api.post(`/chofer/cargas/${pendiente.carga_id}/pendiente`, { falta: pendiente.tipo, motivo, texto });
      toast.success(t("Reported as pending"));
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="border-t pt-4 space-y-3">
      <p className="text-sm font-semibold">{t("I can’t upload it — {contenedor}", { contenedor: pendiente.contenedor })}</p>
      <Select value={motivo} onValueChange={setMotivo}>
        <SelectTrigger data-testid="pendiente-motivo" className="h-11 bg-white"><SelectValue placeholder={t("Reason")} /></SelectTrigger>
        <SelectContent className="bg-white">
          {Object.entries(MOTIVO_PENDIENTE_EN).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
        </SelectContent>
      </Select>
      <Textarea placeholder={t("Details (optional)")} value={texto} onChange={(e) => setTexto(e.target.value)} rows={2} />
      <div className="flex gap-2">
        <Button variant="outline" className="h-11 flex-1" onClick={onClose}>{t("Cancel")}</Button>
        <Button data-testid="pendiente-guardar" className="h-11 flex-1 bg-brand hover:bg-brand-dark text-white" onClick={guardar} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Report")}</Button>
      </div>
    </div>
  );
}
